import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { AUTH, NAV } from "@/constants/testIds";
import { toast } from "sonner";

const QUICK = [
  { role: "recruiter", email: "recruiter@techaifortune.com", password: "Recruit@2026", label: "Recruiter" },
  { role: "admin", email: "admin@techaifortune.com", password: "Admin@2026", label: "Admin" },
  { role: "client", email: "client@northwind.co", password: "Client@2026", label: "Client" },
  { role: "candidate", email: "candidate@demo.com", password: "Candidate@2026", label: "Candidate" },
];

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e, override) => {
    if (e) e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const user = await login(override?.email ?? email, override?.password ?? password);
      toast.success(`Welcome back, ${user.name.split(" ")[0]}`);
      nav("/app");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Login failed";
      setErr(msg); toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#09090B] text-white bg-grid-dark relative">
        <Link to="/" data-testid={NAV.brandLink} className="flex items-center gap-2 relative z-10">
          <img src="/logo-mark.png" alt="" className="h-9 w-9" />
          <span className="font-display font-bold tracking-tight text-white">TechAI Fortune</span>
        </Link>
        <div className="relative z-10">
          <div className="text-xs uppercase tracking-[0.3em] text-white/60 mb-4">Human-in-the-Loop</div>
          <h2 className="font-display text-4xl font-bold tracking-tight leading-tight max-w-md">
            "The copilot drafts. The recruiter decides. The audit remembers."
          </h2>
          <p className="mt-6 text-white/60 max-w-md">TECHAIFORTUNE Workforce Intelligence Cloud — the enterprise staffing platform.</p>
        </div>
        <div className="text-xs text-white/40 relative z-10">© 2026 TECHAIFORTUNE, Inc.</div>
      </div>

      <div className="flex flex-col justify-center p-8 lg:p-16">
        <div className="max-w-md w-full mx-auto">
          <div className="lg:hidden mb-8">
            <Link to="/" className="flex items-center gap-2">
              <div className="h-7 w-7 grid place-items-center rounded-md bg-[#0047AB] text-white font-black text-sm">T</div>
              <span className="font-display font-bold">TECHAIFORTUNE</span>
            </Link>
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tight">Sign in</h1>
          <p className="mt-2 text-muted-foreground">Continue to your workspace.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid={AUTH.loginEmail} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required className="mt-2 h-11" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid={AUTH.loginPassword} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required className="mt-2 h-11" />
            </div>
            {err && <div data-testid={AUTH.loginError} className="text-sm text-destructive">{err}</div>}
            <Button type="submit" data-testid={AUTH.loginSubmit} disabled={loading} className="w-full h-11 bg-[#0047AB] hover:bg-[#003380]">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-8">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Demo accounts</div>
            <div className="grid grid-cols-2 gap-2">
              {QUICK.map((q) => (
                <Button
                  key={q.role}
                  data-testid={AUTH.quickLogin(q.role)}
                  variant="outline"
                  size="sm"
                  onClick={() => submit(null, q)}
                  disabled={loading}
                  className="justify-start font-mono text-xs"
                >
                  <span className="uppercase tracking-wider">{q.label}</span>
                </Button>
              ))}
            </div>
          </div>

          <p className="mt-8 text-sm text-muted-foreground">
            No account? <Link to="/register" className="text-[#0047AB] hover:underline font-medium">Create one</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
